/**
 * RefusalPanel (J6, Wave 3) — recent agent write (wGate) refusals from the
 * gates response `recentRefusals[]`: tool name, refusal line, and when.
 *
 * When the server sent lever context ({lever, currentTier, requiredTier})
 * the lever-aware AC-006 line renders via utils/tiers.formatLeverRefusal
 * ("copy lever is at L2 (Suggest) — L3 (Draft) required.") exactly as the
 * confirmed mock shows, with the server summary as a secondary line when it
 * differs. Refusals are projected from tool trajectories server-side — never
 * model text (existing wGate discipline).
 *
 * Composition per .design/autonomy-facade/mockups/autonomy-page.html.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { AUTONOMY_TEST_IDS } from '../testIds';
import type { AutonomyRefusal } from '../types/gates.types';
import { formatLeverRefusal } from '../utils/tiers';
import { formatRelativeTime } from '../utils/time';

export interface RefusalPanelProps {
  refusals: AutonomyRefusal[];
}

function refusalLine(refusal: AutonomyRefusal): string {
  if (refusal.lever && refusal.currentTier && refusal.requiredTier) {
    return formatLeverRefusal(refusal.lever, refusal.currentTier, refusal.requiredTier);
  }
  return refusal.summary;
}

export const RefusalPanel: React.FC<RefusalPanelProps> = ({ refusals }) => {
  return (
    <Paper data-testid={AUTONOMY_TEST_IDS.refusalPanel} sx={{ mb: 2 }}>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 500, px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        Recent agent write refusals
      </Typography>
      {refusals.map((refusal, index) => {
        const line = refusalLine(refusal);
        return (
          <Box
            key={`${refusal.tool}-${refusal.at ?? index}`}
            data-testid={AUTONOMY_TEST_IDS.refusalAttempt(index)}
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 1.25,
              px: 2.5,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-of-type': { borderBottom: 'none' },
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', color: 'text.secondary', whiteSpace: 'nowrap', flex: 'none' }}
            >
              {refusal.tool}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2">{line}</Typography>
              {line !== refusal.summary && (
                <Typography variant="caption" color="text.secondary">
                  {refusal.summary}
                </Typography>
              )}
            </Box>
            {refusal.at && (
              <Typography variant="caption" sx={{ color: 'text.disabled', whiteSpace: 'nowrap', flex: 'none' }}>
                {formatRelativeTime(refusal.at)}
              </Typography>
            )}
          </Box>
        );
      })}
      {refusals.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 1.5 }}>
          No agent write refusals recorded.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2.5, py: 1 }}>
        Recorded from tool trajectories at the write gate — never from model text.
      </Typography>
    </Paper>
  );
};

export default RefusalPanel;
